/**
 * Public contact page.
 *
 * Three lanes: support (existing customers), sales (pre-sales / pricing
 * questions), and a generic catch-all. The form POSTs to /support-ticket
 * which writes a row to support_tickets and emails support@inboxguard.io
 * via SES. The reply lane drives which inbox alias is notified and how
 * tickets are SLA'd in admin.
 *
 * No auth required — anonymous submissions are allowed (the form just
 * captures the email manually). When the user IS signed in we pre-fill
 * the email so customers don't have to retype it.
 */
import { useState, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Mail, MessageSquare, BookOpen, ShieldCheck, CheckCircle2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { api, ApiHttpError } from '@/lib/api';
import { useAuth } from '@/lib/auth/AuthProvider';
import { analytics } from '@/lib/analytics';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { PublicHeader } from '@/components/PublicHeader';
import { PublicFooter } from '@/components/PublicFooter';

type Topic = 'support' | 'sales' | 'general';

interface TicketResponse {
  id: string;
  status: string;
}

interface TicketBody {
  topic: Topic;
  email: string;
  name: string;
  domain?: string;
  company?: string;
  volume?: string;
  subject: string;
  message: string;
}

const TOPICS: Array<{ id: Topic; label: string; help: string }> = [
  { id: 'support', label: 'Customer support', help: 'Issues with your account, scans, or alerts.' },
  { id: 'sales', label: 'Sales / pricing', help: 'Plan questions, agency tier, custom volumes.' },
  { id: 'general', label: 'General', help: 'Press, partnerships, or anything else.' },
];

const SUBMIT_LABEL: Record<Topic, string> = {
  support: 'Send ticket',
  sales: 'Talk to sales',
  general: 'Send message',
};

const SUBMIT_PENDING: Record<Topic, string> = {
  support: 'Submitting…',
  sales: 'Sending…',
  general: 'Sending…',
};

const SUBJECT_HELP: Record<Topic, { label: string; placeholder: string }> = {
  support: { label: 'Subject', placeholder: 'Short summary of the issue' },
  sales: { label: 'What can we help with?', placeholder: 'Pricing for 50 domains / agency plan / custom volume' },
  general: { label: 'Subject', placeholder: 'What is this about?' },
};

export function ContactPage() {
  const auth = useAuth();
  const [topic, setTopic] = useState<Topic>('support');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [domain, setDomain] = useState('');
  const [company, setCompany] = useState('');
  const [volume, setVolume] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (auth.email && !email) setEmail(auth.email);
  }, [auth.email, email]);

  const submit = useMutation<TicketResponse, ApiHttpError, TicketBody>({
    mutationFn: (body) => api.post<TicketResponse>('/support-ticket', body, { noAuth: !auth.user }),
    onSuccess: (_data, body) => {
      analytics.trackEvent('support_ticket_submitted', { topic: body.topic });
      toast.success('Ticket received. We’ll be in touch by email.');
    },
    onError: (err) => toast.error(err.message),
  });

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    submit.mutate({
      topic,
      email: email.trim(),
      name: name.trim(),
      domain: topic === 'sales' ? undefined : domain.trim() || undefined,
      company: topic === 'sales' ? company.trim() || undefined : undefined,
      volume: topic === 'sales' ? volume.trim() || undefined : undefined,
      subject: subject.trim(),
      message: message.trim(),
    });
  }

  const submitted = submit.isSuccess;

  return (
    <>
      <Helmet>
        <title>Contact — InboxGuard</title>
        <meta
          name="description"
          content="Talk to InboxGuard support, sales, or get help with deliverability problems."
        />
        <link rel="canonical" href="https://inboxguard.io/contact" />
        <meta property="og:title" content="Contact — InboxGuard" />
        <meta property="og:url" content="https://inboxguard.io/contact" />
        <meta property="og:type" content="website" />
        <meta name="robots" content="index,follow" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <PublicHeader />

        <section className="hero-grad">
          <div className="container mx-auto max-w-5xl px-4 pb-12 pt-16 sm:pt-20">
            <div className="mx-auto max-w-2xl text-center animate-fade-in-up">
              <span className="mb-4 inline-flex items-center gap-1.5 rounded-full border border-primary/20 bg-accent px-3 py-1 text-xs font-medium text-accent-foreground">
                <MessageSquare className="h-3 w-3" />
                Talk to a human
              </span>
              <h1 className="text-balance text-4xl font-bold tracking-tight sm:text-5xl">
                We answer every ticket.
              </h1>
              <p className="mx-auto mt-4 max-w-xl text-balance text-lg text-muted-foreground">
                Median first reply under 4 business hours. Tell us what&apos;s going on and we&apos;ll
                route it to the right person.
              </p>
            </div>
          </div>
        </section>

        <main className="container mx-auto max-w-5xl px-4 pb-20 sm:pb-24">
          <div className="grid gap-8 lg:grid-cols-[2fr_1fr]">
            <Card className="card-elev">
              <CardHeader>
                <CardTitle>Send us a message</CardTitle>
                <CardDescription>
                  All tickets are tracked in our admin and replied to from a real person, not a bot.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {submitted ? (
                  <div className="flex flex-col items-center gap-3 rounded-lg border border-success/40 bg-success/5 p-8 text-center">
                    <CheckCircle2 className="h-10 w-10 text-success" />
                    <h2 className="text-lg font-semibold">Your ticket is in the queue.</h2>
                    <p className="max-w-md text-sm text-muted-foreground">
                      We&apos;ll email <span className="font-medium text-foreground">{email}</span> with
                      a reply. Median first response is under 4 business hours.
                    </p>
                    <Button
                      variant="outline"
                      className="mt-2"
                      onClick={() => {
                        setSubject('');
                        setMessage('');
                        submit.reset();
                      }}
                    >
                      Send another
                    </Button>
                  </div>
                ) : (
                  <form className="space-y-5" onSubmit={onSubmit}>
                    <div>
                      <label className="mb-2 block text-sm font-medium">What&apos;s this about?</label>
                      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                        {TOPICS.map((t) => (
                          <button
                            type="button"
                            key={t.id}
                            onClick={() => setTopic(t.id)}
                            className={`rounded-lg border p-3 text-left text-sm transition-colors ${
                              topic === t.id
                                ? 'border-primary bg-accent text-accent-foreground'
                                : 'hover:border-primary/40'
                            }`}
                          >
                            <div className="font-medium">{t.label}</div>
                            <div className="mt-0.5 text-xs text-muted-foreground">{t.help}</div>
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div>
                        <label className="mb-1.5 block text-sm font-medium" htmlFor="contact-name">
                          Your name
                        </label>
                        <Input
                          id="contact-name"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          required
                          maxLength={120}
                        />
                      </div>
                      <div>
                        <label className="mb-1.5 block text-sm font-medium" htmlFor="contact-email">
                          Email
                        </label>
                        <Input
                          id="contact-email"
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          required
                          maxLength={254}
                          disabled={!!auth.email}
                        />
                      </div>
                    </div>

                    {topic === 'support' && (
                      <div>
                        <label className="mb-1.5 block text-sm font-medium" htmlFor="contact-domain">
                          Domain (optional)
                        </label>
                        <Input
                          id="contact-domain"
                          placeholder="yourdomain.com"
                          value={domain}
                          onChange={(e) => setDomain(e.target.value)}
                          maxLength={253}
                        />
                        <p className="mt-1 text-xs text-muted-foreground">
                          If this ticket is about a specific sending domain, drop it here so we can
                          pre-pull the scan history.
                        </p>
                      </div>
                    )}

                    {topic === 'sales' && (
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div>
                          <label className="mb-1.5 block text-sm font-medium" htmlFor="contact-company">
                            Company
                          </label>
                          <Input
                            id="contact-company"
                            placeholder="Acme Co."
                            value={company}
                            onChange={(e) => setCompany(e.target.value)}
                            maxLength={120}
                          />
                        </div>
                        <div>
                          <label className="mb-1.5 block text-sm font-medium" htmlFor="contact-volume">
                            Monthly send volume (optional)
                          </label>
                          <Input
                            id="contact-volume"
                            placeholder="e.g. 500K / month"
                            value={volume}
                            onChange={(e) => setVolume(e.target.value)}
                            maxLength={60}
                          />
                        </div>
                      </div>
                    )}

                    <div>
                      <label className="mb-1.5 block text-sm font-medium" htmlFor="contact-subject">
                        {SUBJECT_HELP[topic].label}
                      </label>
                      <Input
                        id="contact-subject"
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        placeholder={SUBJECT_HELP[topic].placeholder}
                        required
                        maxLength={200}
                      />
                    </div>

                    <div>
                      <label className="mb-1.5 block text-sm font-medium" htmlFor="contact-message">
                        Message
                      </label>
                      <textarea
                        id="contact-message"
                        className="min-h-[160px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        required
                        minLength={10}
                        maxLength={8000}
                      />
                      <p className="mt-1 text-xs text-muted-foreground">
                        The more context the better. Error messages, header dumps, screenshots
                        described in text. We read every word.
                      </p>
                    </div>

                    <Button
                      type="submit"
                      size="lg"
                      disabled={submit.isPending}
                      className="w-full sm:w-auto"
                    >
                      {submit.isPending ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          {SUBMIT_PENDING[topic]}
                        </>
                      ) : (
                        SUBMIT_LABEL[topic]
                      )}
                    </Button>
                  </form>
                )}
              </CardContent>
            </Card>

            <div className="space-y-4">
              <Card className="card-elev">
                <CardContent className="pt-6">
                  <div className="flex h-10 w-10 items-center justify-center rounded-md bg-accent text-accent-foreground">
                    <Mail className="h-5 w-5" />
                  </div>
                  <h3 className="mt-3 text-base font-semibold tracking-tight">Direct email</h3>
                  <p className="mt-2 text-sm text-muted-foreground">
                    Prefer email? Reach us at{' '}
                    <a
                      className="font-medium text-primary hover:underline"
                      href="mailto:support@inboxguard.io"
                    >
                      support@inboxguard.io
                    </a>
                    . Same queue, same SLA.
                  </p>
                </CardContent>
              </Card>

              <Card className="card-elev">
                <CardContent className="pt-6">
                  <div className="flex h-10 w-10 items-center justify-center rounded-md bg-accent text-accent-foreground">
                    <BookOpen className="h-5 w-5" />
                  </div>
                  <h3 className="mt-3 text-base font-semibold tracking-tight">Self-serve first</h3>
                  <p className="mt-2 text-sm text-muted-foreground">
                    Most setup questions are answered in our deliverability guides. Worth a 60-second
                    skim before you write up a ticket.
                  </p>
                  <Link
                    to="/guides"
                    className="mt-3 inline-flex text-sm font-medium text-primary hover:underline"
                  >
                    Browse guides →
                  </Link>
                </CardContent>
              </Card>

              <Card className="card-elev">
                <CardContent className="pt-6">
                  <div className="flex h-10 w-10 items-center justify-center rounded-md bg-accent text-accent-foreground">
                    <ShieldCheck className="h-5 w-5" />
                  </div>
                  <h3 className="mt-3 text-base font-semibold tracking-tight">Security &amp; abuse</h3>
                  <p className="mt-2 text-sm text-muted-foreground">
                    Reporting a vulnerability or abuse on a customer domain? Email{' '}
                    <a className="font-medium text-primary hover:underline" href="mailto:security@inboxguard.io">
                      security@inboxguard.io
                    </a>
                    . PGP available on request.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </main>

        <PublicFooter />
      </div>
    </>
  );
}

export default ContactPage;
